'use client';
import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { Product, milkOptions, syrupOptions, sizes, sizePriceAdder } from './menuData';

interface CustomizeModalProps {
  product: Product;
  onClose: () => void;
  onAdd: (product: Product, size: 'Tall' | 'Grande' | 'Venti', milk: string, syrup: string, extraShot: boolean) => void;
}

export default function CustomizeModal({ product, onClose, onAdd }: CustomizeModalProps) {
  const [size, setSize] = useState<'Tall' | 'Grande' | 'Venti'>('Grande');
  const [milk, setMilk] = useState('Tam Yağlı Süt');
  const [syrup, setSyrup] = useState('Yok');
  const [extraShot, setExtraShot] = useState(false);

  const totalPrice = product.price + sizePriceAdder[size] + (extraShot ? 10 : 0);
  const starsEarned = Math.floor(totalPrice / 10);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center animate-fade-in">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Sheet */}
      <div className="relative w-full max-w-app bg-card rounded-t-3xl animate-slide-up max-h-[90vh] overflow-y-auto scrollbar-hide">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>

        {/* Product header */}
        <div className="flex items-center gap-4 px-5 py-3 border-b border-border">
          <AppImage
            src={product.image}
            alt={product.alt}
            width={72}
            height={72}
            className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
          />
          <div>
            <h2 className="text-foreground font-bold text-lg">{product.name}</h2>
            <p className="text-muted-foreground text-sm">{product.description}</p>
          </div>
          <button onClick={onClose} className="ml-auto w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <Icon name="XMarkIcon" size={18} className="text-muted-foreground" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Size */}
          <div>
            <h3 className="text-foreground font-semibold text-sm mb-3">Boyut</h3>
            <div className="grid grid-cols-3 gap-2">
              {sizes.map((s) => (
                <button
                  key={`size-${s}`}
                  onClick={() => setSize(s)}
                  className={`py-3 rounded-xl border-2 text-center transition-all ${
                    size === s
                      ? 'border-primary bg-accent text-primary' :'border-border bg-card text-foreground hover:border-primary/30'
                  }`}
                >
                  <p className="font-bold text-sm">{s}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {sizePriceAdder[s] === 0 ? 'Standart' : `+₺${sizePriceAdder[s]}`}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Milk */}
          <div>
            <h3 className="text-foreground font-semibold text-sm mb-3">Süt Seçeneği</h3>
            <div className="flex flex-wrap gap-2">
              {milkOptions.map((m) => (
                <button
                  key={`milk-${m}`}
                  onClick={() => setMilk(m)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    milk === m
                      ? 'border-primary bg-primary text-white' :'border-border bg-card text-foreground hover:border-primary/50'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {/* Syrup */}
          <div>
            <h3 className="text-foreground font-semibold text-sm mb-3">Şurup</h3>
            <div className="flex flex-wrap gap-2">
              {syrupOptions.map((s) => (
                <button
                  key={`syrup-${s}`}
                  onClick={() => setSyrup(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    syrup === s
                      ? 'border-primary bg-primary text-white' :'border-border bg-card text-foreground hover:border-primary/50'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Extra shot */}
          <div className="flex items-center justify-between py-3 border-t border-border">
            <div>
              <p className="text-foreground font-semibold text-sm">Ekstra Espresso Shot</p>
              <p className="text-muted-foreground text-xs">+₺10</p>
            </div>
            <button
              onClick={() => setExtraShot(!extraShot)}
              className={`w-12 h-6 rounded-full transition-all duration-200 relative ${
                extraShot ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-200 ${
                  extraShot ? 'left-6' : 'left-0.5'
                }`}
              />
            </button>
          </div>

          {/* Stars preview */}
          <div className="bg-gold-light rounded-xl p-3 flex items-center gap-2">
            <Icon name="StarIcon" size={16} variant="solid" className="text-gold" />
            <span className="text-foreground text-sm font-semibold">
              Bu sipariş ile <span className="text-gold font-bold">{starsEarned} Yıldız</span> kazanırsınız
            </span>
          </div>

          {/* Add to cart */}
          <button
            onClick={() => onAdd(product, size, milk, syrup, extraShot)}
            className="w-full bg-primary text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors active:scale-[0.98]"
          >
            <Icon name="PlusIcon" size={18} className="text-white" />
            <span>Sepete Ekle — ₺{totalPrice}</span>
          </button>
        </div>
      </div>
    </div>
  );
}